import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Card,
  useTheme,
  alpha,
  Stack,
  Avatar,
  Chip,
  IconButton,
  InputAdornment,
  Fade,
  Zoom,
  Autocomplete,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PersonAdd,
  Email,
  ContentCopy,
  Check,
  Groups,
  Celebration,
  RocketLaunch,
  Close,
  AutoAwesome,
  AccountTree,
} from '@mui/icons-material';
import { useTranslate, useGetLocale } from '@refinedev/core';
import axiosInstance from '../../../api/axios-config';

interface SubGroup {
  id: number;
  name: string;
  depth?: number;
  parentId: number | null;
}

interface TeamInviteWalaxyProps {
  onComplete: (invitedUsers: any[]) => void;
  companyId: number;
}

interface TeamMember {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  subGroupId?: number | null;
}

export const TeamInviteWalaxy: React.FC<TeamInviteWalaxyProps> = ({ onComplete, companyId }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const getLocale = useGetLocale();
  const currentLang = getLocale();
  const [loading, setLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([
    { id: '1', email: '', firstName: '', lastName: '', subGroupId: null }
  ]);
  const [sentInvites, setSentInvites] = useState<string[]>([]);
  const [subGroups, setSubGroups] = useState<SubGroup[]>([]);
  const [loadingSubGroups, setLoadingSubGroups] = useState(false);

  // Fetch sub-groups on mount
  useEffect(() => {
    const fetchSubGroups = async () => {
      setLoadingSubGroups(true);
      try {
        const response = await axiosInstance.get<SubGroup[]>('/sub-groups');
        setSubGroups(response.data);
      } catch (err) {
        console.error('Error fetching sub-groups:', err);
      } finally {
        setLoadingSubGroups(false);
      }
    };
    fetchSubGroups();
  }, []);

  const addTeamMember = () => {
    if (teamMembers.length < 5) {
      setTeamMembers([
        ...teamMembers,
        { id: Date.now().toString(), email: '', firstName: '', lastName: '', subGroupId: null }
      ]);
    }
  };

  const removeTeamMember = (id: string) => {
    setTeamMembers(teamMembers.filter(member => member.id !== id));
  };

  const updateTeamMember = (id: string, field: keyof TeamMember, value: string | number | null) => {
    setTeamMembers(teamMembers.map(member =>
      member.id === id ? { ...member, [field]: value } : member
    ));
  };

  const isValidMember = (member: TeamMember) => {
    return member.email && member.firstName && member.lastName;
  };

  const validMembers = teamMembers.filter(isValidMember);

  const generateInviteLink = async () => {
    if (!companyId) {
      console.error('No companyId provided to generateInviteLink');
      return;
    }

    try {
      const response = await axiosInstance.post(`/company/${companyId}/generate-invite-link`);
      setInviteLink(response.data.inviteLink);
    } catch (err) {
      console.error('Error generating invite link:', err);
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleSubmit = async () => {
    setLoading(true);

    console.log('TeamInviteWalaxy - companyId:', companyId);

    if (!companyId) {
      console.error('No companyId provided to TeamInviteWalaxy');
      // Skip the invitation step if no company ID
      onComplete([]);
      return;
    }

    try {
      if (validMembers.length > 0) {
        const response = await axiosInstance.post(`/company/${companyId}/invite-users`, {
          invites: validMembers.map(({ id, ...member }) => member),
          lang: currentLang || 'fr',
        });

        setSentInvites(validMembers.map(m => m.email));
        await generateInviteLink();

        setTimeout(() => {
          onComplete(validMembers);
        }, 2000);
      } else {
        await generateInviteLink();
        onComplete([]);
      }
    } catch (err: any) {
      console.error('Error inviting users:', err);
      setLoading(false);
    }
  };

  const corporateMessages = [
    translate('onboarding.team.messages.collaboration'),
    translate('onboarding.team.messages.productivity'),
    translate('onboarding.team.messages.integration'),
    translate('onboarding.team.messages.network')
  ];

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box
            sx={{
              display: 'inline-block',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.1) rotate(5deg)'
              }
            }}
          >
            <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>
              👥
            </Typography>
          </Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
            {translate('onboarding.team.hero.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {corporateMessages[0]}
          </Typography>
        </Box>
      </motion.div>

      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          { icon: '⚡', label: translate('onboarding.team.stats.immediate'), color: theme.palette.warning.main },
          { icon: '🔒', label: translate('onboarding.team.stats.security'), color: theme.palette.success.main },
          { icon: '🎯', label: translate('onboarding.team.stats.integration'), color: theme.palette.info.main }
        ].map((stat, index) => (
          <Grid item xs={12} md={4} key={index}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                sx={{
                  p: 2,
                  textAlign: 'center',
                  border: `1px solid ${alpha(stat.color, 0.2)}`,
                  bgcolor: alpha(stat.color, 0.05)
                }}
              >
                <Typography variant="h3">{stat.icon}</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stat.label}
                </Typography>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Team Members */}
      <AnimatePresence>
        {teamMembers.map((member, index) => (
          <motion.div
            key={member.id}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card
              sx={{
                p: 3,
                mb: 2,
                border: sentInvites.includes(member.email)
                  ? `2px solid ${theme.palette.success.main}`
                  : '2px solid transparent',
                bgcolor: sentInvites.includes(member.email)
                  ? alpha(theme.palette.success.main, 0.05)
                  : 'transparent'
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    width: 48,
                    height: 48
                  }}
                >
                  {sentInvites.includes(member.email) ? (
                    <Check />
                  ) : (
                    <Typography variant="h6">{index + 1}</Typography>
                  )}
                </Avatar>

                <Box sx={{ flex: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={translate('onboarding.team.form.firstName')}
                        value={member.firstName}
                        onChange={(e) => updateTeamMember(member.id, 'firstName', e.target.value)}
                        disabled={sentInvites.includes(member.email)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={translate('onboarding.team.form.lastName')}
                        value={member.lastName}
                        onChange={(e) => updateTeamMember(member.id, 'lastName', e.target.value)}
                        disabled={sentInvites.includes(member.email)}
                      />
                    </Grid>
                    <Grid item xs={12} md={subGroups.length > 0 ? 3 : 6}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={translate('onboarding.team.form.emailPlaceholder')}
                        type="email"
                        value={member.email}
                        onChange={(e) => updateTeamMember(member.id, 'email', e.target.value)}
                        disabled={sentInvites.includes(member.email)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email fontSize="small" color="action" />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    {subGroups.length > 0 && (
                      <Grid item xs={12} md={3}>
                        <Autocomplete
                          size="small"
                          options={subGroups}
                          getOptionLabel={(option) => option.name}
                          value={subGroups.find((sg) => sg.id === member.subGroupId) || null}
                          onChange={(_, newValue) => updateTeamMember(member.id, 'subGroupId', newValue?.id || null)}
                          disabled={sentInvites.includes(member.email)}
                          loading={loadingSubGroups}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={translate('onboarding.team.form.subGroup')}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <InputAdornment position="start">
                                      <AccountTree fontSize="small" color="action" />
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                              <span style={{ paddingLeft: (option.depth || 0) * 16 }}>
                                {option.name}
                              </span>
                            </li>
                          )}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {teamMembers.length > 1 && !sentInvites.includes(member.email) && (
                  <IconButton
                    onClick={() => removeTeamMember(member.id)}
                    size="small"
                    sx={{ color: theme.palette.error.main }}
                  >
                    <Close />
                  </IconButton>
                )}

                {sentInvites.includes(member.email) && (
                  <Zoom in>
                    <Chip
                      icon={<Celebration />}
                      label={translate('onboarding.team.status.invited')}
                      color="success"
                      size="small"
                    />
                  </Zoom>
                )}
              </Stack>
            </Card>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Add More Button */}
      {teamMembers.length < 5 && sentInvites.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={addTeamMember}
            startIcon={<PersonAdd />}
            sx={{
              py: 2,
              mb: 4,
              borderStyle: 'dashed',
              borderWidth: 2,
              borderRadius: 2,
              '&:hover': {
                borderStyle: 'dashed',
                bgcolor: alpha(theme.palette.primary.main, 0.05)
              }
            }}
          >
            {translate('onboarding.team.buttons.addMember')}
          </Button>
        </motion.div>
      )}

      {/* Invite Link */}
      {inviteLink && (
        <Fade in>
          <Card
            sx={{
              p: 3,
              mb: 4,
              bgcolor: alpha(theme.palette.info.main, 0.05),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
            }}
          >
            <Stack spacing={2}>
              <Typography variant="h6" fontWeight={600}>
                🔗 {translate('onboarding.team.inviteLink.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {translate('onboarding.team.inviteLink.description')}
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth
                  value={inviteLink}
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace' }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={copyInviteLink}
                  startIcon={linkCopied ? <Check /> : <ContentCopy />}
                  sx={{ minWidth: 120 }}
                >
                  {linkCopied ? translate('onboarding.team.buttons.copied') : translate('onboarding.team.buttons.copy')}
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Fade>
      )}

      {/* CTA Buttons */}
      <Stack direction="row" spacing={2} sx={{ mt: 6 }}>
        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={() => onComplete([])}
          disabled={loading}
          sx={{
            py: 2,
            fontWeight: 600
          }}
        >
          {translate('onboarding.team.buttons.later')}
        </Button>
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={sentInvites.length > 0 ? <RocketLaunch /> : <Groups />}
          sx={{
            py: 2,
            fontWeight: 600,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`
            }
          }}
        >
          {loading
            ? translate('onboarding.team.buttons.sending')
            : sentInvites.length > 0
              ? translate('onboarding.team.buttons.continue')
              : validMembers.length > 0
                ? translate('onboarding.team.buttons.invite', { count: validMembers.length })
                : translate('onboarding.team.buttons.skipInvite')
          }
        </Button>
      </Stack>

      {/* Trust Badge */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Chip
          icon={<AutoAwesome />}
          label={translate('onboarding.team.trustBadge')}
          size="small"
          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
        />
      </Box>
    </Box>
  );
};
